if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.activeMediaIndex = 0;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.isDraggingMedia = false;
        this.mediaTransitionDuration = 430;
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handleModalPointerUpCapture = this.handleModalPointerUpCapture.bind(this);
        this.handleWheelNavigation = this.handleWheelNavigation.bind(this);
        this.previousButton = this.querySelector('.product-media-modal__nav-button--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav-button--next');
        this.zoomButton = this.querySelector('.product-media-modal__zoom-toggle');
        this.previousButton?.addEventListener('click', () => this.changeActiveMedia(-1));
        this.nextButton?.addEventListener('click', () => this.changeActiveMedia(1));
        this.previousButton?.addEventListener('pointerup', (event) => event.stopPropagation());
        this.nextButton?.addEventListener('pointerup', (event) => event.stopPropagation());
        this.zoomButton?.addEventListener('click', (event) => this.handleZoomButtonClick(event));
        this.zoomButton?.addEventListener('pointerup', (event) => event.stopPropagation());
        this.addEventListener('keydown', this.handleKeyDown);
        this.addEventListener('wheel', this.handleWheelNavigation, { passive: false, capture: true });
        this.addEventListener('pointerup', this.handleModalPointerUpCapture, { capture: true });
        this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        this.addEventListener('touchmove', this.handleTouchMove, { passive: true });
        this.addEventListener('touchend', this.handleTouchEnd);
        this.mediaContent = this.querySelector('.product-media-modal__content');
        this.mediaContent?.addEventListener('click', this.handleMediaClick.bind(this));
        this.mediaContent?.addEventListener('pointerdown', this.handlePointerDown);
        this.mediaContent?.addEventListener('pointermove', this.handlePointerMove);
        this.mediaContent?.addEventListener('pointerup', this.handlePointerUp);
        this.mediaContent?.addEventListener('pointercancel', this.handlePointerUp);
        this.mediaContent?.addEventListener('dragstart', (event) => event.preventDefault());
        this.mediaContent?.addEventListener('pointerup', (event) => {
          if (event.target.nodeName === 'IMG') event.stopPropagation();
        });
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.refreshMediaItems();
        this.showActiveMedia(opener?.getAttribute('data-media-id'));
        this.queueSlideDistanceUpdate();
      }

      refreshMediaItems() {
        this.mediaItems = Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
        this.currentCounter = this.querySelector('[data-product-modal-current]');
        this.totalCounter = this.querySelector('[data-product-modal-total]');

        this.mediaItems.forEach((element) => {
          if (element.nodeName === 'IMG') element.draggable = false;
          element.querySelectorAll?.('img').forEach((image) => {
            image.draggable = false;
            if (!image.dataset.productModalSizingBound) {
              image.dataset.productModalSizingBound = 'true';
              image.addEventListener('load', () => this.queueSlideDistanceUpdate());
            }
          });
        });

        if (this.totalCounter) this.totalCounter.textContent = this.mediaItems.length;
      }

      showActiveMedia(mediaId) {
        const mediaIndex = this.mediaItems.findIndex((element) => element.getAttribute('data-media-id') === mediaId);
        this.setActiveMedia(mediaIndex >= 0 ? mediaIndex : 0, 0);
      }

      changeActiveMedia(direction) {
        if (!this.mediaItems || this.mediaItems.length < 2) return;
        this.updateModalSlideDistances();
        this.setActiveMedia(this.activeMediaIndex + direction, direction);
      }

      getModalSlideDistance(direction = 1) {
        if (!this.mediaContent) return window.innerWidth;

        const slideGap = parseFloat(getComputedStyle(this.mediaContent).getPropertyValue('--modal-slide-gap')) || 0;

        if (window.matchMedia('(max-width: 749px)').matches) {
          const mobileDistance = window.innerWidth + slideGap;
          this.mediaContent.style.setProperty('--modal-previous-distance', `${mobileDistance}px`);
          this.mediaContent.style.setProperty('--modal-next-distance', `${mobileDistance}px`);
          return mobileDistance;
        }

        const distances = this.updateModalSlideDistances();
        return direction < 0 ? distances.previous : distances.next;
      }

      getMediaWidth(element) {
        if (!element) return 0;

        const mediaElement = element.nodeName === 'IMG' ? element : element.querySelector('img, video, model-viewer');
        const rect = (mediaElement || element).getBoundingClientRect();

        return rect.width || 0;
      }

      updateModalSlideDistances() {
        if (!this.mediaContent || !this.mediaItems || this.mediaItems.length === 0) {
          return { previous: window.innerWidth, next: window.innerWidth };
        }

        const mediaCount = this.mediaItems.length;
        const slideGap = parseFloat(getComputedStyle(this.mediaContent).getPropertyValue('--modal-slide-gap')) || 0;
        const activeMedia = this.mediaItems[this.activeMediaIndex];
        const previousMedia = this.mediaItems[(this.activeMediaIndex - 1 + mediaCount) % mediaCount];
        const nextMedia = this.mediaItems[(this.activeMediaIndex + 1) % mediaCount];
        const activeWidth = this.getMediaWidth(activeMedia) || window.innerWidth;
        const previousWidth = this.getMediaWidth(previousMedia) || activeWidth;
        const nextWidth = this.getMediaWidth(nextMedia) || activeWidth;
        const previousDistance = activeWidth / 2 + previousWidth / 2 + slideGap;
        const nextDistance = activeWidth / 2 + nextWidth / 2 + slideGap;

        this.mediaContent.style.setProperty('--modal-previous-distance', `${previousDistance}px`);
        this.mediaContent.style.setProperty('--modal-next-distance', `${nextDistance}px`);

        return { previous: previousDistance, next: nextDistance };
      }

      queueSlideDistanceUpdate() {
        window.requestAnimationFrame(() => {
          this.updateModalSlideDistances();
          window.requestAnimationFrame(() => this.updateModalSlideDistances());
        });
      }

      setActiveMedia(index, direction = 0) {
        if (!this.mediaItems || this.mediaItems.length === 0) return;

        this.resetZoom();

        const mediaCount = this.mediaItems.length;
        this.activeMediaIndex = (index + mediaCount) % mediaCount;
        const activeMedia = this.mediaItems[this.activeMediaIndex];
        this.positionMediaItems(direction);

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        const container = this.querySelector('[role="document"]');
        container.scrollTo({ top: 0, left: 0, behavior: 'auto' });

        if (this.currentCounter) this.currentCounter.textContent = this.activeMediaIndex + 1;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      positionMediaItems(direction = 0, isDragging = false) {
        if (!this.mediaItems || this.mediaItems.length === 0) return;

        const mediaCount = this.mediaItems.length;
        const previousIndex = (this.activeMediaIndex - 1 + mediaCount) % mediaCount;
        const nextIndex = (this.activeMediaIndex + 1) % mediaCount;
        const carouselClasses = [
          'product-media-modal__media--previous',
          'product-media-modal__media--next',
        ];

        this.mediaItems.forEach((element, elementIndex) => {
          const isActive = elementIndex === this.activeMediaIndex;
          const isTwoItemNeighbor = mediaCount === 2 && !isActive;
          const twoItemIsPrevious = isDragging ? direction < 0 : direction > 0;
          const twoItemIsNext = isDragging ? direction >= 0 : direction <= 0;
          const isPrevious =
            mediaCount > 2
              ? elementIndex === previousIndex
              : isTwoItemNeighbor && twoItemIsPrevious;
          const isNext =
            mediaCount > 2
              ? elementIndex === nextIndex
              : isTwoItemNeighbor && twoItemIsNext;

          element.classList.remove(...carouselClasses);
          element.classList.toggle('active', isActive);
          element.classList.toggle('product-media-modal__media--previous', isPrevious);
          element.classList.toggle('product-media-modal__media--next', isNext);
          element.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        this.updateModalSlideDistances();
        this.queueSlideDistanceUpdate();
      }

      handleZoomButtonClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const activeImage = this.mediaItems?.[this.activeMediaIndex]?.nodeName === 'IMG'
          ? this.mediaItems[this.activeMediaIndex]
          : this.mediaItems?.[this.activeMediaIndex]?.querySelector('img');

        if (!activeImage) return;

        activeImage.style.setProperty('--zoom-origin-x', '50%');
        activeImage.style.setProperty('--zoom-origin-y', '50%');
        activeImage.classList.toggle('product-media-modal__image--zoomed');
        this.classList.toggle(
          'product-media-modal--zoomed',
          activeImage.classList.contains('product-media-modal__image--zoomed')
        );
      }

      handleMediaClick(event) {
        if (!this.hasAttribute('open')) return;

        const activeMedia = this.mediaItems?.[this.activeMediaIndex];
        const image =
          event.target.nodeName === 'IMG'
            ? event.target
            : event.target.closest?.('img') ||
              (activeMedia?.nodeName === 'IMG' ? activeMedia : activeMedia?.querySelector('img'));

        if (!image || (activeMedia !== image && !activeMedia?.contains?.(image))) return;

        event.preventDefault();
        event.stopPropagation();

        if (this.skipNextMediaClick) {
          this.skipNextMediaClick = false;
          window.clearTimeout(this.skipNextMediaClickTimeout);
          return;
        }

        const rect = image.getBoundingClientRect();
        const originX = ((event.clientX - rect.left) / rect.width) * 100;
        const originY = ((event.clientY - rect.top) / rect.height) * 100;

        image.style.setProperty('--zoom-origin-x', `${originX}%`);
        image.style.setProperty('--zoom-origin-y', `${originY}%`);
        image.classList.toggle('product-media-modal__image--zoomed');
        this.classList.toggle('product-media-modal--zoomed', image.classList.contains('product-media-modal__image--zoomed'));
      }

      resetZoom() {
        this.classList.remove('product-media-modal--zoomed');
        this.querySelectorAll('.product-media-modal__image--zoomed').forEach((image) => {
          image.classList.remove('product-media-modal__image--zoomed');
        });
      }

      handleKeyDown(event) {
        if (!this.hasAttribute('open')) return;
        if (event.code.toUpperCase() === 'ARROWLEFT') {
          event.preventDefault();
          this.changeActiveMedia(-1);
        }
        if (event.code.toUpperCase() === 'ARROWRIGHT') {
          event.preventDefault();
          this.changeActiveMedia(1);
        }
      }

      handleTouchStart(event) {
        if (!this.hasAttribute('open')) return;
        this.startMediaDrag(event.changedTouches[0].screenX, event.changedTouches[0].screenY);
      }

      handleTouchMove(event) {
        if (!this.hasAttribute('open') || !this.touchStartX || !this.mediaContent) return;
        if (!this.mediaItems || this.mediaItems.length < 2 || this.classList.contains('product-media-modal--zoomed')) return;

        this.moveMediaDrag(event.changedTouches[0].screenX, event.changedTouches[0].screenY);
      }

      handleTouchEnd(event) {
        if (!this.hasAttribute('open') || !this.touchStartX) return;
        this.finishMediaDrag(event.changedTouches[0].screenX);
      }

      handlePointerDown(event) {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        if (!this.hasAttribute('open') || event.target.closest('button')) return;
        if (!this.mediaItems || this.mediaItems.length < 2 || this.classList.contains('product-media-modal--zoomed')) return;

        event.preventDefault();
        this.activePointerId = event.pointerId;
        this.mediaContent?.setPointerCapture?.(event.pointerId);
        this.startMediaDrag(event.clientX, event.clientY);
      }

      handlePointerMove(event) {
        if (event.pointerType === 'touch' || event.pointerId !== this.activePointerId) return;
        if (!this.hasAttribute('open') || !this.touchStartX || !this.mediaContent) return;

        event.preventDefault();
        this.moveMediaDrag(event.clientX, event.clientY);
      }

      handlePointerUp(event) {
        if (event.pointerType === 'touch' || event.pointerId !== this.activePointerId) return;

        this.mediaContent?.releasePointerCapture?.(event.pointerId);
        this.activePointerId = null;
        this.finishMediaDrag(event.clientX);
      }

      handleModalPointerUpCapture(event) {
        if (event.pointerType !== 'mouse') return;
        if (!event.target.closest('.product-media-modal__content')) return;

        this.handlePointerUp(event);
        event.stopPropagation();
      }

      handleWheelNavigation(event) {
        if (!this.hasAttribute('open') || !this.mediaItems || this.mediaItems.length < 2) return;
        if (Math.abs(event.deltaX) < 12 || Math.abs(event.deltaX) < Math.abs(event.deltaY)) return;

        event.preventDefault();
        event.stopPropagation();

        if (this.classList.contains('product-media-modal--zoomed') || this.wheelNavigationLocked) return;
        if (Math.abs(event.deltaX) < 40) return;

        this.wheelNavigationLocked = true;
        this.slideActiveMedia(event.deltaX > 0 ? 1 : -1);

        window.setTimeout(() => {
          this.wheelNavigationLocked = false;
        }, this.mediaTransitionDuration);
      }

      startMediaDrag(startX, startY) {
        this.touchStartX = startX;
        this.touchStartY = startY;
        this.isDraggingMedia = false;
      }

      ignoreNextMediaClick() {
        this.skipNextMediaClick = true;
        window.clearTimeout(this.skipNextMediaClickTimeout);
        this.skipNextMediaClickTimeout = window.setTimeout(() => {
          this.skipNextMediaClick = false;
        }, 550);
      }

      moveMediaDrag(currentX, currentY) {
        const touchDistanceX = currentX - this.touchStartX;
        const touchDistanceY = currentY - this.touchStartY;

        if (Math.abs(touchDistanceX) < 8 || Math.abs(touchDistanceX) < Math.abs(touchDistanceY)) return;

        const direction = touchDistanceX > 0 ? -1 : 1;
        const dragLimit = this.getModalSlideDistance(direction);
        const dragOffset = Math.max(Math.min(touchDistanceX, dragLimit), dragLimit * -1);

        this.isDraggingMedia = true;
        this.classList.add('product-media-modal--dragging');
        this.mediaContent.style.setProperty('--modal-drag-offset', `${dragOffset}px`);
        this.positionMediaItems(direction, true);
      }

      slideActiveMedia(direction) {
        if (!this.mediaItems || this.mediaItems.length < 2 || !this.mediaContent) return;

        const slideDistance = this.getModalSlideDistance(direction);

        this.ignoreNextMediaClick();
        this.classList.add('product-media-modal--sliding');
        this.positionMediaItems(direction, true);
        this.mediaContent.style.setProperty('--modal-drag-offset', '0px');
        this.mediaContent.offsetWidth;
        this.mediaContent.style.setProperty('--modal-drag-offset', `${direction * slideDistance * -1}px`);

        window.setTimeout(() => {
          this.classList.add('product-media-modal--dragging');
          this.changeActiveMedia(direction);
          this.mediaContent?.style.removeProperty('--modal-drag-offset');
          this.mediaContent?.offsetWidth;
          this.classList.remove('product-media-modal--dragging');
          this.classList.remove('product-media-modal--sliding');
        }, this.mediaTransitionDuration);
      }

      finishMediaDrag(endX) {
        const touchDistance = endX - this.touchStartX;
        const direction = touchDistance > 0 ? -1 : 1;
        const slideDistance = this.getModalSlideDistance(direction);
        const slideThreshold = Math.min(Math.max(window.innerWidth * 0.18, 64), 120);
        const wasDraggingMedia = this.isDraggingMedia;
        this.touchStartX = 0;
        this.touchStartY = 0;

        if (this.isDraggingMedia) {
          this.classList.remove('product-media-modal--dragging');
          this.isDraggingMedia = false;
        }

        if (Math.abs(touchDistance) < slideThreshold) {
          if (wasDraggingMedia) this.ignoreNextMediaClick();
          this.mediaContent?.style.removeProperty('--modal-drag-offset');
          this.positionMediaItems(0);
          return;
        }

        this.ignoreNextMediaClick();
        this.classList.add('product-media-modal--sliding');
        this.mediaContent?.style.setProperty('--modal-drag-offset', `${direction * slideDistance * -1}px`);

        window.setTimeout(() => {
          this.classList.add('product-media-modal--dragging');
          this.changeActiveMedia(direction);
          this.mediaContent?.style.removeProperty('--modal-drag-offset');
          this.mediaContent?.offsetWidth;
          this.classList.remove('product-media-modal--dragging');
          this.classList.remove('product-media-modal--sliding');
        }, this.mediaTransitionDuration);
      }
    }
  );
}
